/**
 * JWT Utility Functions
 *
 * Utilities for decoding JWT tokens and extracting expiration information
 * without verification (client-side only, for expiration checking)
 */

/**
 * Decode JWT token without verification
 * Returns the payload as an object
 */
export const decodeJWT = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode base64url encoded payload
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch (error) {
    console.error('[jwtUtils] Error decoding JWT:', error);
    return null;
  }
};

/**
 * Extract expiration time from JWT token
 * Returns expiration as Date or null if not found/invalid
 */
export const getTokenExpiration = (token: string): Date | null => {
  const payload = decodeJWT(token);
  if (!payload) {
    return null;
  }

  const exp = payload.exp;
  if (typeof exp !== 'number') {
    return null;
  }

  // JWT exp is in seconds, convert to milliseconds
  return new Date(exp * 1000);
};

/**
 * Check if token is expired
 * @param token - JWT token string
 * @param bufferSeconds - Optional buffer time in seconds before considering token expired (default: 0)
 * @returns true if token is expired or invalid, false otherwise
 */
export const isTokenExpired = (token: string, bufferSeconds: number = 0): boolean => {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    // If we can't determine expiration, consider it expired for safety
    return true;
  }

  const now = new Date();
  const bufferMs = bufferSeconds * 1000;
  return expiration.getTime() <= now.getTime() + bufferMs;
};

/**
 * Calculate expiration time from expiresIn seconds
 * @param expiresIn - Number of seconds until expiration
 * @returns Unix timestamp in milliseconds
 */
export const calculateExpirationTime = (expiresIn: number): number => {
  return Date.now() + expiresIn * 1000;
};



